import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { ToastMessage } from "primereact/toast";
import { DeliveryAssignmentOptions } from "../../models/delivery-planning";
import orderServices from "../../services/orders/orderservice";
import { calendarDate, dateForCalendar, indiaToday, newRequestId } from "../../utils/delivery-planning";

interface Props {
  orderId: number;
  visible: boolean;
  onHide: () => void;
  onAssigned?: () => void | Promise<void>;
  notify: (title: string, message: string, severity: ToastMessage["severity"]) => void;
}

const OrderDeliveryAssignmentDialog = ({ orderId, visible, onHide, onAssigned, notify }: Props) => {
  const [options, setOptions] = useState<DeliveryAssignmentOptions | null>(null);
  const [loading, setLoading] = useState(false);
  const [lookupFailed, setLookupFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const [executiveId, setExecutiveId] = useState<number | null>(null);
  const [vehicleNo, setVehicleNo] = useState<string | null>(null);
  const [sequenceNo, setSequenceNo] = useState<number | null>(null);
  const [date, setDate] = useState<Date | null>(null);
  const [saving, setSaving] = useState(false);
  const working = useRef(false);
  const generation = useRef(0);
  const pending = useRef<{ key: string; id: string } | null>(null);

  useEffect(() => {
    const current = ++generation.current;
    if (visible && orderId > 0) {
      setOptions(null); setExecutiveId(null); setVehicleNo(null); setSequenceNo(null);
      setDate(dateForCalendar(indiaToday())); setLookupFailed(false); setLoading(true); pending.current = null;
      orderServices.getDeliveryAssignmentOptions().then(result => {
        if (generation.current !== current) return;
        setOptions(result); setDate(dateForCalendar(result.today > indiaToday() ? result.today : indiaToday()));
      }).catch(() => {
        if (generation.current === current) setLookupFailed(true);
      }).finally(() => {
        if (generation.current === current) setLoading(false);
      });
    }
    return () => { generation.current = current + 1; };
  }, [visible, orderId, reload]);

  const today = indiaToday();
  const earliest = options && options.today > today ? options.today : today;
  const executive = options?.executives.find(e => e.id === executiveId);
  const valid = orderId > 0 && !!executive?.phone?.trim() && !!vehicleNo &&
    !!options?.vehicles.some(v => v.name === vehicleNo) && sequenceNo !== null &&
    Number.isInteger(sequenceNo) && sequenceNo > 0 && sequenceNo <= 2147483647 &&
    !!date && !Number.isNaN(date.getTime()) && calendarDate(date) >= earliest;

  const assign = async () => {
    if (working.current || loading || !valid || !date || !executive || !sequenceNo || !vehicleNo) return;
    working.current = true; setSaving(true);
    const current = generation.current;
    const deliveryDate = calendarDate(date);
    const deliveries = [{ orderID: orderId, primaryContactNumber: executive.phone.trim(), sequenceNo, vehicleNo }];
    let saved = false;
    try {
      const key = JSON.stringify({ deliveryDate, deliveries });
      if (pending.current?.key !== key) pending.current = { key, id: newRequestId() };
      const response = await orderServices.importDriverList(deliveries, deliveryDate, pending.current.id);
      if (generation.current !== current) return;
      if (!response?.isSuccess) {
        notify("Assign Order", response?.message || "The order could not be assigned. Review the details and retry.", "error");
        return;
      }
      saved = true; pending.current = null;
      notify("Assign Order", `Order assigned for ${date.toLocaleDateString("en-GB")}.`, "success");
      onHide();
      await onAssigned?.();
    } catch (error: unknown) {
      if (saved) {
        notify("Assign Order", "The order was assigned, but refreshing failed. Refresh the order list.", "warn");
      } else if (generation.current === current) {
        const message = axios.isAxiosError<{ message?: string }>(error) ? error.response?.data?.message : undefined;
        notify("Assign Order", message || "Could not confirm the assignment. Retry with the same details.", "error");
      }
    } finally { working.current = false; setSaving(false); }
  };

  return <Dialog header="Assign Order" visible={visible} modal style={{ width: "min(95vw, 560px)" }}
    closable={!saving} closeOnEscape={!saving} onHide={() => { if (!working.current) onHide(); }}>
    <div className="p-fluid">
      <div className="field"><label htmlFor="assign-order-number">Order No</label>
        <input id="assign-order-number" className="p-inputtext p-component" value={orderId || ""} readOnly /></div>
      {lookupFailed && <div role="alert" className="mb-3">
        <p>Driver and vehicle choices could not be loaded.</p>
        <Button label="Retry loading choices" outlined onClick={() => setReload(v => v + 1)} disabled={saving || loading} />
      </div>}
      <div className="field"><label htmlFor="assign-order-executive">Select Driver *</label>
        <Dropdown inputId="assign-order-executive" value={executiveId}
          options={options?.executives.map(e => ({ ...e, label: `${e.name}, ${e.phone}` })) ?? []}
          optionLabel="label" optionValue="id" filter placeholder={loading ? "Loading choices..." : "Search for User/Mobile"}
          disabled={loading || saving || !options} onChange={e => setExecutiveId(e.value)} /></div>
      <div className="field"><label htmlFor="assign-order-vehicle">Select Vehicle *</label>
        <Dropdown inputId="assign-order-vehicle" value={vehicleNo} options={options?.vehicles ?? []}
          optionLabel="name" optionValue="name" filter placeholder="Choose Vehicle"
          disabled={loading || saving || !options} onChange={e => setVehicleNo(e.value)} /></div>
      <div className="field"><label htmlFor="assign-order-sequence">Sequence No *</label>
        <input id="assign-order-sequence" className="p-inputtext p-component" type="number" min={1} step={1}
          max={2147483647} value={sequenceNo ?? ""} disabled={loading || saving || !options}
          onChange={e => setSequenceNo(e.target.value === "" ? null : Number(e.target.value))} placeholder="Enter sequence no" /></div>
      <div className="field"><label htmlFor="assign-order-date">Planned delivery date *</label>
        <Calendar inputId="assign-order-date" value={date} minDate={dateForCalendar(earliest)} dateFormat="dd/mm/yy"
          showIcon readOnlyInput disabled={loading || saving || !options} onChange={e => setDate(e.value as Date | null)} />
        <small>Choose today or a future date (IST). This order appears in Delivery Assessment only on the selected date.</small></div>
      <div className="flex justify-content-end gap-2 mt-3">
        <Button label="Cancel" outlined disabled={saving} onClick={onHide} />
        <Button label="Assign" icon="pi pi-check" loading={saving} disabled={!valid || loading || saving} onClick={() => void assign()} />
      </div>
    </div>
  </Dialog>;
};

export default OrderDeliveryAssignmentDialog;
